/**
 * ============================================================================
 * MASTER SERVICE: Country & Pricing Service (Sheets 10, 11, 12)
 * ============================================================================
 * Purpose:
 *   Handles international country catalogs, operator base rates, and
 *   custom negotiated sales prices per customer account.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import api from "../../core/services/api";
import { createCrudService } from "../../core/services/crudServiceFactory";

const baseCountryService = createCrudService("countries");
const countryOperatorService = createCrudService("country-operators");
const accountCountryService = createCrudService("account-countries");

const countryService = {
  ...baseCountryService,

  // Nested mapping services
  operators: countryOperatorService,
  accountRates: accountCountryService,

  /**
   * Retrieves official Meta rate card.
   */
  getOfficialMetaRates: () => {
    return api.get("/countries/official-meta-rates");
  },

  /**
   * Retrieves custom country rates and permissions for a specific account.
   */
  getAccountCountrySettings: (accountId) => {
    return api.get(`/countries/account-rates/${accountId}`);
  },

  /**
   * Saves custom country rates and international destination permissions for an account.
   */
  saveAccountCountrySettings: (accountId, payload) => {
    return api.post(`/countries/account-rates/${accountId}`, payload);
  },

  /**
   * Retrieves account-wise user messaging & delivery policy settings
   */
  getAccountUserSettings: (accountId) => {
    return api.get(`/countries/account-user-settings/${accountId}`);
  },

  /**
   * Saves account-wise user messaging & delivery policy settings
   */
  saveAccountUserSettings: (accountId, payload) => {
    return api.post(`/countries/account-user-settings/${accountId}`, payload);
  },

  /**
   * Retrieves effective message rate for a specific account and destination country.
   *
   * @param {number} accountId
   * @param {number} countryId
   * @param {string} [category="marketing"]
   * @returns {Promise<Object>} Effective rate payload
   */
  getEffectiveRate: (accountId, countryId, category = "marketing") => {
    return api.get(`/countries/rate/${accountId}/${countryId}?category=${category}`);
  },
};

export default countryService;
